import { createActionGroup, emptyProps, props, createAction } from '@ngrx/store'
import {
    SelectionMode,
    FreeSelectionsForCase,
    SelectionType,
    FreeSelection,
    HierarchicalSelectionRoot,
    HierarchicalSelectionsForCase,
    UnsupervisedSelectionRoot,
} from './selection.state'
import { HttpErrorResponse } from '@angular/common/http'
import {
    FreeSelectionQueryParams,
    HierarchicalSelectionQueryParams,
} from '../../shared/model/query-params-types'

export const selectionActions = createActionGroup({
    source: 'Selection',
    events: {
        'Set Type': props<{ selectionType: SelectionType }>(),
        'Set Mode': props<{ mode: SelectionMode }>(),
        'Set Resolution': props<{ resolution: number }>(),
        'Update Selection': props<{ segSource: string; points: number[] }>(),
        'Clear Selection': props<{ allBuckets?: boolean }>(),
        'Set Active Seg Source': props<{ segSource: string | undefined }>(),
        'Loading Free Selections Succeeded': props<{
            selections: FreeSelectionsForCase
            queryParams: FreeSelectionQueryParams
        }>(),
        'Loading Free Selections Failed': props<{ error: HttpErrorResponse; message: string }>(),
        'Loading Hierarchical Selection List Succeeded': props<{ list: string[] }>(),
        'Loading Hierarchical Selection List Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Loading Hierarchical Selections Succeeded': props<{
            selections: HierarchicalSelectionsForCase
            queryParams: HierarchicalSelectionQueryParams
        }>(),
        'Loading Hierarchical Selections Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Loading Unsupervised Selection List Succeeded': props<{ list: string[] }>(),
        'Loading Unsupervised Selection List Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Loading Unsupervised Selections Succeeded': props<{
            selections: HierarchicalSelectionsForCase
            queryParams: HierarchicalSelectionQueryParams
        }>(),
        'Loading Unsupervised Selections Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Set Free Selection': props<{ name: string | undefined }>(),
        'Set Hierarchical Selection Root': props<{ name: string }>(),
        'Set Hierarchical Selection Node': props<{ name: string | undefined }>(),
        'Set Auto Cell Type Detection Root': props<{ name: string }>(),
        'Set Auto Cell Type Detection Node': props<{ name: string | undefined }>(),
        'Set Visible Selections On Scatter Plot': props<{ selectionNames: string[] }>(),
        'Save New Free Selection': props<{ name: string }>(),
        'Save Free Selection': emptyProps(),
        'Save New Hierarchical Selection Root': props<{ name: string; segSources: string[] }>(),
        'Save New Auto Cell Type Detection Root': props<{ name: string; segSources: string[] }>(),
        'Clear Active Hierarchical Selection Root': emptyProps(),
        'Clear Active Unsupervised Selection Root': emptyProps(),
        'Save New Hierarchical Selection Node': props<{ name: string }>(),
        'Save New Auto Cell Type Detection Node': props<{ name: string }>(),
        'Saving Free Selection Succeeded': props<{ selection: FreeSelection }>(),
        'Saving Free Selection Failed': props<{ error: HttpErrorResponse; message: string }>(),
        'Saving Hierarchical Selection Root Succeeded': props<{
            selection: HierarchicalSelectionRoot
        }>(),
        'Saving Hierarchical Selection Root Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Saving Auto Cell Type Detection Root Succeeded': props<{
            selection: UnsupervisedSelectionRoot
        }>(),
        'Saving Auto Cell Type Detection Root Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Rename Free Selection': props<{ name: string; newName: string }>(),
        'Renaming Free Selection Succeeded': props<{ name: string; newName: string }>(),
        'Renaming Free Selection Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Rename Hierarchical Selection Root': props<{ name: string; newName: string }>(),
        'Renaming Hierarchical Selection Root Succeeded': props<{
            name: string
            newName: string
        }>(),
        'Renaming Hierarchical Selection Root Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Rename Hierarchical Selection': props<{
            rootName: string
            name: string
            newName: string
        }>(),
        'Renaming Hierarchical Selection Succeeded': props<{
            rootName: string
            name: string
            newName: string
        }>(),
        'Renaming Hierarchical Selection Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Rename Unsupervised Selection Root': props<{ name: string; newName: string }>(),
        'Renaming Unsupervised Selection Root Succeeded': props<{
            name: string
            newName: string
        }>(),
        'Renaming Unsupervised Selection Root Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Rename Unsupervised Selection': props<{
            rootName: string
            name: string
            newName: string
        }>(),
        'Renaming Unsupervised Selection Succeeded': props<{
            rootName: string
            name: string
            newName: string
        }>(),
        'Renaming Unsupervised Selection Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Delete Free Selection': props<{ name: string }>(),
        'Delete Hierarchical Selection Root': props<{ name: string }>(),
        'Delete Auto Cell Type Detection Root': props<{ name: string }>(),
        'Delete Hierarchical Selection Node': emptyProps(),
        'Delete Hierarchical Selection Node Recursively': props<{
            groupName: string
            name: string
        }>(),
        'Delete Auto Cell Type Detection Node': emptyProps(),
        'Delete Auto Cell Type Detection Node Recursively': props<{
            groupName: string
            name: string
        }>(),
        'Deleting Free Selection Succeeded': props<{ name: string }>(),
        'Deleting Free Selection Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Deleting Hierarchical Selection Root Succeeded': props<{ name: string }>(),
        'Deleting Hierarchical Selection Root Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Deleting Auto Cell Type Detection Root Succeeded': props<{ name: string }>(),
        'Deleting Auto Cell Type Detection Root Failed': props<{
            error: HttpErrorResponse
            message: string
        }>(),
        'Set Selection Visibility': props<{
            selectionName: string | undefined
            selectionType: SelectionType
            selectionRoot: string | undefined
            visible: boolean
        }>(),
        'Set Selection Opacity': props<{
            selectionName: string | undefined
            selectionType: SelectionType
            selectionRoot: string | undefined
            opacity: number
        }>(),
        'Set Selection Shader Color': props<{
            selectionName: string | undefined
            selectionType: SelectionType
            selectionRoot: string | undefined
            color: string
        }>(),
        'Set Search Key': props<{ searchKey: string }>(),
    },
})

export const noOpAction = createAction('[No Operation] No Operation')
